import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'

import * as gs from '../gStyles'

import Text from './Text'

export type LabelProps = c.r<{
  alignCenter?: boolean
  alignRight?: boolean
  children: number | string | (number | string)[]
  danger?: boolean
  disabled?: boolean
  iconRight?: React.ReactElement | false
  on: c.Surface
  onPress?: c.VoidFn
  showAsterisk?: boolean
  small?: boolean
}>

export default React.memo<LabelProps>(function Label({
  alignCenter,
  alignRight,
  children,
  danger,
  disabled,
  iconRight,
  on,
  onPress,
  showAsterisk,
  small,
}) {
  const styles = gs.useThemedStyleSheet(themedStyles, on)

  const Wrapper = onPress && !disabled ? rn.TouchableOpacity : rn.View

  const containerStyle = alignRight
    ? styles.containerAlignRight
    : alignCenter
    ? styles.containerAlignCenter
    : gs.rowSpaceBetween100

  return (
    // prettier-ignore
    <Wrapper
      onPress={disabled ? undefined : onPress}
      style={containerStyle}
    >
      <Text
        disableAndroidExtraSpace
        ellipsizeMode="tail"
        style={(() => {
          if (small && disabled) {
            return alignRight
              ? styles.labelDisabledAlignRightSmall
              : alignCenter
              ? styles.labelDisabledAlignCenterSmall
              : styles.labelDisabledSmall
          }
          if (disabled) {
            return alignRight
              ? styles.labelDisabledAlignRight
              : alignCenter
              ? styles.labelDisabledAlignCenter
              : styles.labelDisabled
          }
          if (small && danger) {
            return alignRight
              ? styles.labelDangerAlignRightSmall
              : alignCenter
              ? styles.labelDangerAlignCenterSmall
              : styles.labelDangerSmall
          }
          if (danger) {
            return alignRight
              ? styles.labelDangerAlignRight
              : alignCenter
              ? styles.labelDangerAlignCenter
              : styles.labelDanger
          }
          if (small) {
            return alignRight
              ? styles.labelAlignRightSmall
              : alignCenter
              ? styles.labelAlignCenterSmall
              : styles.labelSmall
          }
          return alignRight
            ? styles.labelAlignRight
            : alignCenter
            ? styles.labelAlignCenter
            : styles.label
        })()}
      >
        {rn.Platform.OS === 'android'? children+ ' ' : children} {showAsterisk && <Text disableAndroidExtraSpace style={styles.required}>{'* '}</Text>}
      </Text>
      {iconRight}
    </Wrapper>
  )
})

const themedStyles = gs.ThemedStyleSheet.create((t, on) => {
  const small = { fontSize: 15 }

  const label: rn.TextStyle = {
    color: t.input[on].labelColor,
    flexShrink: 1,
    fontFamily: t.input[on].labelFontFamily,
    fontSize: t.input[on].labelFontSize,
    fontStyle: t.input[on].labelItalic ? 'italic' : 'normal',
    // fontWeight: '500',
    letterSpacing: 0.8,
    textTransform: t.input[on].labelUppercase ? 'uppercase' : undefined,
    ...gs.userSelectNone,
  } as const
  const labelAlignRight = { ...label, ...gs.txtAlignRight }
  const labelAlignCenter = { ...label, textAlign: 'center' as const }
  const danger = { color: t.danger }
  const labelDanger = { ...label, ...danger }
  const labelDangerAlignRight = { ...label, ...danger, ...gs.txtAlignRight }
  const labelDangerAlignCenter = {
    ...label,
    ...danger,
    textAlign: 'center' as const,
  }
  const labelDisabled = { ...label, ...gs.disabled }
  const labelDisabledAlignRight = {
    ...label,
    ...gs.disabled,
    ...gs.txtAlignRight,
  }
  const labelDisabledAlignCenter = {
    ...label,
    ...gs.disabled,
    textAlign: 'center' as const,
  }

  return {
    containerAlignRight: {
      ...gs.rowSpaceBetween100,
      justifyContent: 'flex-end',
    },
    containerAlignCenter: {
      ...gs.rowSpaceBetween100,
      justifyContent: 'center',
    },
    label,
    labelSmall: { ...label, ...small },
    labelAlignRight,
    labelAlignRightSmall: { ...labelAlignRight, ...small },
    labelAlignCenter,
    labelAlignCenterSmall: { ...labelAlignCenter, ...small },
    labelDanger,
    labelDangerSmall: { ...labelDanger, ...small },
    labelDangerAlignRight,
    labelDangerAlignRightSmall: { ...labelDangerAlignRight, ...small },
    labelDangerAlignCenter,
    labelDangerAlignCenterSmall: { ...labelDangerAlignCenter, ...small },
    labelDisabled,
    labelDisabledSmall: { ...labelDisabled, ...small },
    labelDisabledAlignRight,
    labelDisabledAlignRightSmall: { ...labelDisabledAlignRight, ...small },
    labelDisabledAlignCenter,
    labelDisabledAlignCenterSmall: { ...labelDisabledAlignCenter, ...small },
    required: { color: t.third, fontFamily: t[on].fontFamily },
  }
})
